/**
 * @fileoverview The published video walkthroughs: one entry per film, in the
 * order each portal's list shows them.
 *
 * Every surface that shows a walkthrough reads it from here: the Onboarding
 * hub (/academy/onboarding), the film's own page (/academy/onboarding/:slug,
 * the link people share), and any feature page that leads with one. Not
 * /onboarding: that is the wizard a new account is sent through after signup. Publishing the next film
 * is one entry and its three files under public/media/walkthroughs/: the
 * captioned MP4 (rendered and mastered in video/, then compressed for the
 * web), a poster frame, and the narration as WebVTT.
 *
 * `chapters` are the film's step boundaries (video/script/<film>-vo.md), so a
 * viewer can jump to the step they came for and share a link that starts
 * there (?t=seconds).
 */

/**
 * The portals walkthroughs are grouped under, in the order the hub lists
 * them. Read top to bottom, the hub is a sequence: the organization's
 * introduction, the instructor's, then joining a program, then the student's.
 */
export const WALKTHROUGH_PORTALS = [
  {
    id: "organization",
    title: "Organization portal",
    blurb: "For the people who run programs, staff classes and keep the records.",
  },
  {
    id: "instructor",
    title: "Instructor portal",
    blurb: "For instructors teaching with an organization, or running their own studio.",
  },
  {
    id: "student",
    title: "Student portal",
    blurb: "For students, and the parents who follow along.",
  },
];

const media = (slug) => ({
  src: `/media/walkthroughs/${slug}.mp4`,
  poster: `/media/walkthroughs/${slug}.jpg`,
  captions: `/media/walkthroughs/${slug}.vtt`,
});

export const WALKTHROUGHS = [
  {
    slug: "introducing-hans-students",
    portal: "student",
    title: "Introducing Hans for Students",
    summary:
      "See what's next, find any lesson from any teacher, read a summary, see how your parent stays in the loop, and manage your teachers.",
    seconds: 110,
    published: "2026-10-06",
    ...media("introducing-hans-students"),
    chapters: [
      { at: 0, title: "Welcome" },
      { at: 7.5, title: "Find your way around" },
      { at: 17.5, title: "See what's next" },
      { at: 27.5, title: "Find any lesson" },
      { at: 39, title: "Read a summary" },
      { at: 58, title: "Your portfolio" },
      { at: 64.5, title: "Family and privacy" },
      { at: 77.5, title: "Your profile and teachers" },
    ],
  },
  {
    slug: "introducing-hans-instructors",
    portal: "instructor",
    title: "Introducing Hans for Instructors",
    summary:
      "Add a student, ask for consent, record or type your lesson notes, edit them, attach a file, and share it with the student and their parent.",
    seconds: 163,
    published: "2026-10-01",
    ...media("introducing-hans-instructors"),
    chapters: [
      { at: 0, title: "Welcome" },
      { at: 8.5, title: "Find your way around" },
      { at: 21, title: "Add a student" },
      { at: 42, title: "Ask for consent" },
      { at: 52, title: "Type lesson notes" },
      { at: 78, title: "Record a lesson" },
      { at: 109, title: "Edit the notes" },
      { at: 120, title: "Attach a file" },
      { at: 126.5, title: "Share with families" },
      { at: 151.5, title: "See your week" },
    ],
  },
  {
    slug: "managing-your-students",
    portal: "instructor",
    title: "Managing Your Students",
    summary:
      "Keep private reminders, note what each student loves, invite them to their own portal, and see what happens if they move on.",
    seconds: 131,
    published: "2026-10-06",
    ...media("managing-your-students"),
    chapters: [
      { at: 0, title: "Welcome" },
      { at: 7.5, title: "Your roster" },
      { at: 16, title: "The student profile" },
      { at: 30.5, title: "Note their interests" },
      { at: 56.8, title: "Invite to the portal" },
      { at: 73.5, title: "Watch them join" },
      { at: 97, title: "No chat, just summaries" },
      { at: 111, title: "If a student leaves" },
    ],
  },
  {
    slug: "instructor-first-week",
    portal: "instructor",
    title: "Joining Your First Program",
    summary:
      "Join your organization, accept your program, plan your first class with prep notes, and write up your first lesson.",
    seconds: 84,
    published: "2026-10-01",
    ...media("instructor-first-week"),
    chapters: [
      { at: 0, title: "Welcome" },
      { at: 7.5, title: "Join your organization" },
      { at: 21, title: "Accept your program" },
      { at: 28.5, title: "Open your class" },
      { at: 38.5, title: "Add prep notes" },
      { at: 54, title: "See what's coming up" },
      { at: 61.5, title: "Write your lesson notes" },
    ],
  },
  {
    slug: "introducing-hans-organizations",
    portal: "organization",
    title: "Introducing Hans for Organizations",
    summary:
      "Publish a program, invite your instructors, give a class its schedule, set reminders and substitute rules, and send your first announcement.",
    seconds: 168,
    published: "2026-10-01",
    ...media("introducing-hans-organizations"),
    chapters: [
      { at: 0, title: "Welcome" },
      { at: 8.5, title: "Find your way around" },
      { at: 22, title: "Create a program" },
      { at: 51, title: "Add the details" },
      { at: 64, title: "Invite instructors" },
      { at: 82, title: "Add a class" },
      { at: 97, title: "Set the schedule" },
      { at: 126, title: "Remind about notes" },
      { at: 137.5, title: "Set substitute rules" },
      { at: 150.5, title: "Send an announcement" },
    ],
  },
  {
    slug: "sharing-a-program",
    portal: "organization",
    title: "Sharing a Program",
    summary:
      "Invite a school contact to follow a program, choose what they see, and tour their view: lessons, attendance, recording consent and the weekly recap.",
    seconds: 134.5,
    published: "2026-10-06",
    ...media("sharing-a-program"),
    chapters: [
      { at: 0, title: "Welcome" },
      { at: 7.5, title: "Add a viewer" },
      { at: 21.5, title: "Choose what they see" },
      { at: 42, title: "They set a password" },
      { at: 56, title: "The overview" },
      { at: 78, title: "Lessons" },
      { at: 95, title: "Students and calendar" },
      { at: 111, title: "Their settings" },
      { at: 123, title: "The weekly recap" },
    ],
  },
];

/**
 * The hub's shelves: each portal with its films, in list order, leaving out
 * a portal with nothing published yet rather than showing an empty heading.
 */
export function shelvesOf(films = WALKTHROUGHS) {
  return WALKTHROUGH_PORTALS.map((portal) => ({ ...portal, films: films.filter((w) => w.portal === portal.id) })).filter(
    (shelf) => shelf.films.length > 0,
  );
}

/** The walkthrough at `slug`, or undefined. */
export function walkthroughBySlug(slug) {
  return WALKTHROUGHS.find((w) => w.slug === slug);
}

/** "1:24" from seconds. */
export function formatDuration(seconds) {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The address a walkthrough is shared at, optionally starting at a moment. */
export function walkthroughPath(slug, at = 0) {
  return `/academy/onboarding/${slug}${at > 0 ? `?t=${Math.floor(at)}` : ""}`;
}
